import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Transaction, Invoice } from "@/types/database";
import { summarizeTransactions } from "@/lib/dashboard/statMath";
import { useCurrentUser } from "./useCurrentUser";
import { useOrganization } from "./useOrganization";
import { orgScopedKey } from "@/lib/queryKeys";
import { fetchAllRows, fetchInChunks } from "@/lib/fetchAllRows";
import { yearBoundsFromDates, type YearBounds } from "@/lib/yearRange";
import type { LinkableReceipt } from "@/lib/receipts/receiptTransactionLink";

// Every list here is bounded (a year, a month, a set of ids) and read through
// fetchAllRows/fetchInChunks: hosted PostgREST cuts any un-ranged select at 1000 rows
// without an error, which silently dropped the oldest transactions and matches.
//
// Keys stay under the "transactions" / "matches-by-transaction" prefixes so every
// existing invalidation reaches them. Array-valued "transactions" queries must hold
// Transaction rows: TransactionsTable and the Transactions page patch every one of
// them optimistically.

/** Local date formatting: toISOString() would shift midnight to the previous day east of UTC. */
const formatLocalDate = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

/** First and last day of a month, as `date` columns store them. */
function monthRange(year: number, month: number) {
  return {
    start: formatLocalDate(new Date(year, month - 1, 1)),
    end: formatLocalDate(new Date(year, month, 0)),
  };
}

/**
 * @deprecated only kept for pages being rewritten in parallel PRs; use useTransactionsForYear
 *
 * Every transaction of the organization, all years. Paged past the row cap so the list
 * is complete, but it grows without bound: new code reads a bounded hook instead.
 */
export function useTransactions() {
  const { data: user } = useCurrentUser();
  const { currentOrganization } = useOrganization();
  const orgId = currentOrganization?.id;

  return useQuery({
    queryKey: orgScopedKey("transactions", orgId ?? ""),
    queryFn: async ({ signal }) => {
      const rows = await fetchAllRows(
        () =>
          supabase
            .from("transactions")
            .select("*")
            .eq("organization_id", orgId!)
            .abortSignal(signal),
        { order: [{ column: "date", ascending: false }], signal }
      );
      return rows as unknown as Transaction[];
    },
    enabled: !!user && !!orgId,
  });
}

/**
 * Every transaction of one calendar year, newest first (`date desc`, then `id desc`).
 *
 * The key carries a literal "year" segment: FetchTransactionButton refetches
 * `["transactions", org, <year>, <month>]` as the month query, which a bare year in
 * slot 2 would collide with.
 */
export function useTransactionsForYear(year: number, options?: { enabled?: boolean }) {
  const { data: user } = useCurrentUser();
  const { currentOrganization } = useOrganization();
  const orgId = currentOrganization?.id;

  return useQuery({
    queryKey: orgScopedKey("transactions", orgId ?? "", "year", year),
    enabled: options?.enabled !== false && !!user && !!orgId,
    // A busy year is several pages; don't re-read them on every navigation.
    staleTime: 60 * 1000,
    queryFn: async ({ signal }) => {
      const rows = await fetchAllRows(
        () =>
          supabase
            .from("transactions")
            .select("*")
            .eq("organization_id", orgId!)
            .gte("date", `${year}-01-01`)
            .lte("date", `${year}-12-31`)
            .abortSignal(signal),
        { order: [{ column: "date", ascending: false }], signal }
      );
      return rows as unknown as Transaction[];
    },
  });
}

/**
 * The years of the organization's oldest and newest dated transaction, or null when it
 * has none. Feeds the year selectors, which used to collect years from the all-time list.
 *
 * Returns an object, not an array, so the optimistic patchers (which rewrite every
 * array-valued "transactions" query) leave it alone.
 */
export function useTransactionYearBounds() {
  const { data: user } = useCurrentUser();
  const { currentOrganization } = useOrganization();
  const orgId = currentOrganization?.id;

  return useQuery({
    queryKey: orgScopedKey("transactions", orgId ?? "", "year-bounds"),
    enabled: !!user && !!orgId,
    staleTime: 5 * 60 * 1000,
    queryFn: async ({ signal }): Promise<YearBounds | null> => {
      const edge = (ascending: boolean) =>
        supabase
          .from("transactions")
          .select("date")
          .eq("organization_id", orgId!)
          .not("date", "is", null)
          .order("date", { ascending })
          .limit(1)
          .abortSignal(signal)
          .maybeSingle();

      const [{ data: oldest, error: oldestError }, { data: newest, error: newestError }] =
        await Promise.all([edge(true), edge(false)]);
      if (oldestError) throw oldestError;
      if (newestError) throw newestError;
      return yearBoundsFromDates(oldest?.date, newest?.date);
    },
  });
}

/**
 * The transactions the given receipts are linked to — exactly what
 * isReceiptMatched/findTransactionForReceipt need, instead of the whole ledger:
 * - by id, for every receipt that names its transaction (`receipts.transaction_id`)
 * - by `transactions.receipt_id` / `invoice_id`, for receipts not marked matched by
 *   their own columns (legacy rows written before every match path set them)
 *
 * No receipts with ids to look up means no request.
 */
export function useTransactionsLinkedToReceipts(
  receipts: LinkableReceipt[] | undefined,
  options?: { enabled?: boolean }
) {
  const { data: user } = useCurrentUser();
  const { currentOrganization } = useOrganization();
  const orgId = currentOrganization?.id;

  const unique = (ids: (string | null | undefined)[]) =>
    [...new Set(ids.filter((id): id is string => Boolean(id)))].sort();
  const linkedIds = unique((receipts ?? []).map((r) => r.transaction_id));
  const legacyReceiptIds = unique(
    (receipts ?? []).filter((r) => !(r.matched && r.transaction_id)).map((r) => r.id)
  );

  return useQuery({
    queryKey: orgScopedKey(
      "transactions",
      orgId ?? "",
      "linked-to-receipts",
      linkedIds,
      legacyReceiptIds
    ),
    enabled: options?.enabled !== false && !!user && !!orgId && receipts !== undefined,
    // A new filter or year changes the ids; keep the old answer on screen meanwhile.
    placeholderData: keepPreviousData,
    // Toggling a search back and forth returns to the same ids; every match/unmatch
    // invalidates the "transactions" prefix anyway.
    staleTime: 30 * 1000,
    queryFn: async ({ signal }) => {
      if (linkedIds.length === 0 && legacyReceiptIds.length === 0) return [];

      const [byId, byLegacyColumn] = await Promise.all([
        fetchInChunks(linkedIds, (chunk) =>
          supabase
            .from("transactions")
            .select("*")
            .eq("organization_id", orgId!)
            .in("id", chunk)
            .abortSignal(signal)
        ),
        // Each receipt id appears twice in the URL, hence the smaller chunks.
        fetchInChunks(
          legacyReceiptIds,
          (chunk) =>
            supabase
              .from("transactions")
              .select("*")
              .eq("organization_id", orgId!)
              .or(`receipt_id.in.(${chunk.join(",")}),invoice_id.in.(${chunk.join(",")})`)
              .abortSignal(signal),
          { chunkSize: 50 }
        ),
      ]);

      const byTransactionId = new Map<string, Transaction>();
      for (const row of [...byId, ...byLegacyColumn] as unknown as Transaction[]) {
        byTransactionId.set(row.id, row);
      }
      return [...byTransactionId.values()];
    },
  });
}

/**
 * How many of the year's transactions are not matched yet — the same figure
 * the Párování overview shows as "N nespárováno". Head-only count, so the
 * sidebar can show it on every page without pulling the rows. Keyed under
 * "transactions", so the existing match/unmatch invalidations refresh it.
 */
export function useUnmatchedTransactionCount(year: number, options?: { enabled?: boolean }) {
  const { data: user } = useCurrentUser();
  const { currentOrganization } = useOrganization();
  const orgId = currentOrganization?.id;

  return useQuery({
    queryKey: orgScopedKey("transactions", orgId ?? "", "unmatched-count", year),
    enabled: options?.enabled !== false && !!user && !!orgId,
    staleTime: 30 * 1000,
    queryFn: async () => {
      const { count, error } = await supabase
        .from("transactions")
        .select("id", { count: "exact", head: true })
        .eq("organization_id", orgId!)
        .gte("date", `${year}-01-01`)
        .lte("date", `${year}-12-31`)
        // NOT true (not `= false`): a null flag counts as unmatched, as in summarizeTransactions.
        .not("matched", "is", true);

      if (error) throw error;
      return count ?? 0;
    },
  });
}

export interface MatchedDocument {
  matchId: string;
  invoice: Invoice;
  matchScore: number | null;
}

/**
 * The documents matched to each transaction of one month, keyed by transaction id —
 * what the month's transaction table shows in its document column.
 *
 * Scoped to the month through the transaction's date (`transactions!inner`), so it is
 * a month's worth of rows rather than every match the organization ever made.
 */
export function useMatchesByTransaction(
  year: number,
  month: number,
  options?: { enabled?: boolean }
) {
  const { data: user } = useCurrentUser();
  const { currentOrganization } = useOrganization();
  const orgId = currentOrganization?.id;

  return useQuery({
    queryKey: orgScopedKey("matches-by-transaction", orgId ?? "", "month", year, month),
    enabled: options?.enabled !== false && !!user && !!orgId,
    queryFn: async ({ signal }) => {
      const { start, end } = monthRange(year, month);
      const data = await fetchAllRows(
        () =>
          supabase
            .from("matches")
            .select(
              "*, invoices(*, clients:client_id(name, company_name)), receipts(*), transactions!inner(date)"
            )
            .eq("organization_id", orgId!)
            // !inner makes the embed a join, so these keep only the month's matches.
            .gte("transactions.date", start)
            .lte("transactions.date", end)
            .abortSignal(signal),
        { order: [{ column: "created_at", ascending: false }], signal }
      );

      // Group matches by transaction_id; receipt matches are presented like
      // invoice matches for display purposes.
      const grouped = data.reduce(
        (acc, match) => {
          const txId = match.transaction_id;
          if (!txId) return acc;
          if (!acc[txId]) acc[txId] = [];

          if (match.receipts && match.receipt_id) {
            acc[txId].push({
              matchId: match.id,
              invoice: match.receipts as unknown as Invoice,
              matchScore: match.match_score,
            });
          } else if (match.invoices && match.invoice_id) {
            acc[txId].push({
              matchId: match.id,
              invoice: match.invoices as unknown as Invoice,
              matchScore: match.match_score,
            });
          }
          return acc;
        },
        {} as Record<string, MatchedDocument[]>
      );

      return grouped;
    },
  });
}

/**
 * Which of these invoices are matched to any transaction, in any month. The month's
 * matches alone cannot say that an invoice was already settled by a payment in another
 * month, and the manual-match dropdown would offer it again.
 */
export function useMatchedInvoiceIds(
  invoiceIds: readonly string[],
  options?: { enabled?: boolean }
) {
  const { data: user } = useCurrentUser();
  const { currentOrganization } = useOrganization();
  const orgId = currentOrganization?.id;
  const ids = [...new Set(invoiceIds)].sort();

  return useQuery({
    queryKey: orgScopedKey("matches-by-transaction", orgId ?? "", "invoice-ids", ids),
    enabled: options?.enabled !== false && !!user && !!orgId,
    queryFn: async ({ signal }) => {
      if (ids.length === 0) return new Set<string>();
      const rows = await fetchInChunks(ids, (chunk) =>
        supabase
          .from("matches")
          .select("invoice_id")
          .eq("organization_id", orgId!)
          .in("invoice_id", chunk)
          .abortSignal(signal)
      );
      return new Set(rows.map((row) => row.invoice_id).filter((id): id is string => Boolean(id)));
    },
  });
}

/**
 * The match rows of one transaction, newest first: enough for the preview dialog to
 * remove exactly one pair, and to refuse when it cannot tell which.
 */
export function useTransactionMatches(
  transactionId: string | null | undefined,
  options?: { enabled?: boolean }
) {
  const { data: user } = useCurrentUser();
  const { currentOrganization } = useOrganization();
  const orgId = currentOrganization?.id;

  return useQuery({
    queryKey: orgScopedKey("matches-by-transaction", orgId ?? "", "tx", transactionId ?? ""),
    enabled: options?.enabled !== false && !!user && !!orgId && !!transactionId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("matches")
        .select("id, match_score, receipt_id, invoice_id")
        .eq("organization_id", orgId!)
        .eq("transaction_id", transactionId!)
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data ?? [];
    },
  });
}

export function useTransactionsByMonth(
  year: number,
  month: number,
  options?: { enabled?: boolean }
) {
  const { data: user } = useCurrentUser();
  const { currentOrganization } = useOrganization();
  const orgId = currentOrganization?.id;

  return useQuery({
    queryKey: orgScopedKey("transactions", orgId ?? "", year, month),
    enabled: options?.enabled !== false && !!user && !!orgId,
    queryFn: async () => {
      // Local date formatting prevents UTC timezone shifts.
      const formatDate = (d: Date) =>
        `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

      const startDate = formatDate(new Date(year, month - 1, 1));
      const endDate = formatDate(new Date(year, month, 0));

      const { data, error } = await supabase
        .from("transactions")
        .select("*")
        .eq("organization_id", orgId!)
        .gte("date", startDate)
        .lte("date", endDate)
        .order("date", { ascending: false });

      if (error) throw error;
      return (data || []) as Transaction[];
    },
    refetchOnWindowFocus: true,
    refetchOnMount: true,
    staleTime: 0,
    gcTime: 0,
  });
}

export function getMonthStats(
  transactions: Transaction[],
  year: number,
  month: number,
  excludeIncoming: boolean = false
) {
  let filtered = transactions.filter((t) => {
    if (!t.date) return false;
    const d = new Date(t.date);
    return d.getFullYear() === year && d.getMonth() + 1 === month;
  });

  // Filter out incoming (positive) transactions if enabled
  if (excludeIncoming) {
    filtered = filtered.filter((t) => {
      if (t.amount === null) return true;
      return t.amount <= 0;
    });
  }

  const summary = summarizeTransactions(filtered);

  return {
    count: filtered.length,
    matched: summary.matched,
    income: summary.income,
    expense: summary.expense,
    matchRate: summary.matchRate,
  };
}
